import { useEffect, useState } from "react";
import { PAYMENT_METHOD_LABELS, PAYMENT_METHODS, ROLES } from "@dentacore/shared-types";
import type {
  AccountingLocation,
  AccountingYear,
  ExpenseCategory,
  ExpenseRow,
  RoleName,
  SalaryPerson,
} from "@dentacore/shared-types";

import { listExpenses } from "../api/accounting";
import { describe } from "../settings/use-writer";
import { roleLabel } from "../settings/people-section";
import { PagedRows } from "../ui/paging";
import { MonthSelect, YearPicker } from "./controls";
import {
  money,
  monthName,
  monthStarted,
  niceDay,
  niceDayShort,
  percent,
  periodRange,
} from "./format";
import { useModules } from "../shell/modules-context";
import styles from "./accounting.module.css";

/** Everything a period adds up to, merged across its months. */
interface Merged {
  moneyIn: number;
  materials: number;
  salaries: number;
  expenses: number;
  profit: number;
  byMethod: Map<string, number>;
  byLocation: Map<string, number>;
  bySupplier: Map<string, { name: string; cents: number }>;
  byPerson: Map<string, { name: string; job: RoleName; cents: number }>;
  byCategory: Map<string, number>;
}

function add<K>(map: Map<K, number>, key: K, cents: number): void {
  map.set(key, (map.get(key) ?? 0) + cents);
}

/**
 * Breakdown — everything paid and received in a year or a month (STORY-172).
 *
 * Money in by how it was paid and by location; money out as salaries by
 * person, materials by supplier and expenses by category, where a category
 * opens onto the expenses in it. **The group totals are the months' own
 * totals, not the sum of the lines under them**, so the last line —
 * money in less money out — is exactly the profit Overview shows for the same
 * period.
 */
export function BreakdownTab({
  data,
  year,
  yearOptions,
  onYear,
  branches,
  locations,
  selected,
  categories,
  salaries,
  seesPay,
  seesSpending,
  currency,
  selectionLabel,
  version,
  fail,
}: {
  data: AccountingYear | null;
  year: number;
  yearOptions: readonly number[];
  onYear: (year: number) => void;
  /** The selected location ids, comma-separated, for the expense list. */
  branches: string;
  locations: readonly AccountingLocation[];
  selected: readonly string[];
  categories: readonly ExpenseCategory[] | null;
  salaries: readonly SalaryPerson[] | null;
  /**
   * Whether each person's pay and each expense line may be shown (QA4-D8).
   * Without the right the totals stay and the lines say why they are not
   * there; the server strips them too.
   */
  seesPay: boolean;
  seesSpending: boolean;
  currency: string;
  selectionLabel: string;
  /** Moves after every write, so the expense list is read again. */
  version: number;
  fail: (message: string) => void;
}): React.JSX.Element {
  // Materials are Supply's: without it there is no Materials tab (QF-D4).
  const hasSupply = useModules().has("SUPPLY");
  // 0 is the whole year.
  const [pickedMonth, setPickedMonth] = useState(0);
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const [rows, setRows] = useState<readonly ExpenseRow[] | null>(null);

  // A month picked in another year may not have begun in this one.
  const month =
    data !== null && pickedMonth !== 0 && monthStarted(year, pickedMonth, data.today)
      ? pickedMonth
      : 0;
  const ready = data !== null && data.year === year;

  // The expenses behind the category lines, for when one is opened.
  useEffect(() => {
    if (!ready || !seesSpending) return;
    const controller = new AbortController();
    const { from, to } = periodRange(year, month);
    setRows(null);
    listExpenses(from, to, branches, controller.signal)
      .then(setRows)
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setRows([]);
        fail(describe(cause, "The expenses could not be read."));
      });
    return () => controller.abort();
  }, [ready, year, month, branches, version, fail, seesSpending]);

  const controls = (
    <div className={styles.controls}>
      <YearPicker years={yearOptions} value={year} onChange={onYear} testId="accounting-year" />
      {data !== null && (
        <MonthSelect
          year={year}
          today={data.today}
          value={month}
          onChange={(next) => {
            setPickedMonth(next);
            setOpen(new Set());
          }}
          label="Period"
          testId="accounting-breakdown-month"
        />
      )}
      {data !== null && ready && (
        <p className={styles.note}>
          Showing {periodLabel(year, month, data)}, {selectionLabel}.
        </p>
      )}
    </div>
  );

  if (data === null || !ready) {
    return (
      <>
        {controls}
        <div className={styles.card}>
          <p className={styles.blank}>Loading…</p>
        </div>
      </>
    );
  }

  /* ── merge the months in the period ───────────────────────────────────── */

  const merged: Merged = {
    moneyIn: 0,
    materials: 0,
    salaries: 0,
    expenses: 0,
    profit: 0,
    byMethod: new Map(),
    byLocation: new Map(),
    bySupplier: new Map(),
    byPerson: new Map(),
    byCategory: new Map(),
  };
  for (const row of data.months) {
    if (!row.started || (month !== 0 && row.month !== month)) continue;
    merged.moneyIn += row.moneyIn;
    merged.materials += row.materials;
    merged.salaries += row.salaries;
    merged.expenses += row.expenses;
    merged.profit += row.profit;
    for (const [method, cents] of Object.entries(row.byMethod))
      add(merged.byMethod, method, cents ?? 0);
    for (const [branchId, cents] of Object.entries(row.byLocation))
      add(merged.byLocation, branchId, cents);
    for (const line of row.bySupplier) {
      const was = merged.bySupplier.get(line.supplierId);
      merged.bySupplier.set(line.supplierId, {
        name: line.name,
        cents: (was?.cents ?? 0) + line.cents,
      });
    }
    for (const line of row.byPerson) {
      const was = merged.byPerson.get(line.userId);
      merged.byPerson.set(line.userId, {
        name: line.name,
        job: line.job,
        cents: (was?.cents ?? 0) + line.cents,
      });
    }
    for (const line of row.byCategory) add(merged.byCategory, line.categoryId, line.cents);
  }

  const moneyOut = merged.materials + merged.salaries + merged.expenses;
  // The bars are drawn against the larger side, so in and out compare at a glance.
  const scale = Math.max(1, merged.moneyIn, moneyOut);
  const partial = selected.length < locations.length;

  const people = [...merged.byPerson.entries()]
    .filter(([, line]) => line.cents !== 0)
    .sort(
      ([, a], [, b]) => ROLES.indexOf(a.job) - ROLES.indexOf(b.job) || a.name.localeCompare(b.name),
    );
  const suppliers = [...merged.bySupplier.entries()]
    .filter(([, line]) => line.cents !== 0)
    .sort(([, a], [, b]) => b.cents - a.cents || a.name.localeCompare(b.name));
  const categoryName = (id: string): string =>
    (categories ?? []).find((category) => category.id === id)?.name ?? "A category";
  const spent = [...merged.byCategory.entries()]
    .filter(([, cents]) => cents !== 0)
    .sort(([a, x], [b, y]) => y - x || categoryName(a).localeCompare(categoryName(b)));
  const locationName = (id: string): string =>
    locations.find((location) => location.id === id)?.name ?? "Another location";

  /** Joined or left during this year — the two reasons a salary looks short. */
  const personNote = (userId: string, job: RoleName): string => {
    const person = (salaries ?? []).find((row) => row.userId === userId);
    const parts = [roleLabel(job)];
    if (person?.endedOn != null && person.endedOn <= data.today) {
      parts.push(`left ${niceDay(person.endedOn)}`);
    }
    if (person?.startedOn != null && person.startedOn.startsWith(`${String(year)}-`)) {
      parts.push(`joined ${niceDay(person.startedOn)}`);
    }
    return parts.join(" · ");
  };

  /**
   * **Money out one list at a time** (fit every screen size, 2026-10-17): its
   * three lists stacked ran a laptop's window 260px over even with salaries
   * paged. Each tab carries its total, so the card still reads at a glance.
   */
  const [outTab, setOutTab] = useState<"salaries" | "materials" | "expenses">("salaries");

  const toggle = (categoryId: string): void =>
    setOpen((was) => {
      const next = new Set(was);
      if (next.has(categoryId)) next.delete(categoryId);
      else next.add(categoryId);
      return next;
    });

  return (
    <>
      {controls}

      <div className={styles.breakdownGrid}>
        <section className={styles.card} data-testid="accounting-money-in">
          <div className={styles.cardHead}>
            <h2 className={styles.cardTitle}>Money in</h2>
            <span className={styles.spacer} />
            <b className={styles.num}>{money(merged.moneyIn, currency)}</b>
          </div>
          <div className={styles.group}>
            <GroupHead
              label="Payments received"
              note="by how they paid"
              cents={merged.moneyIn}
              currency={currency}
            />
            {PAYMENT_METHODS.filter((method) => (merged.byMethod.get(method) ?? 0) !== 0).map(
              (method) => {
                const cents = merged.byMethod.get(method) ?? 0;
                return (
                  <Line
                    key={method}
                    label={PAYMENT_METHOD_LABELS[method]}
                    sub={
                      merged.moneyIn > 0
                        ? `${percent(cents / merged.moneyIn)} of money in`
                        : undefined
                    }
                    cents={cents}
                    scale={scale}
                    currency={currency}
                  />
                );
              },
            )}
            {merged.moneyIn === 0 && <p className={styles.blank}>None</p>}
          </div>
          {selected.length > 1 && (
            <div className={styles.group}>
              <GroupHead label="By location" cents={merged.moneyIn} currency={currency} />
              {selected
                .filter((id) => (merged.byLocation.get(id) ?? 0) !== 0)
                .map((id) => (
                  <Line
                    key={id}
                    label={locationName(id)}
                    cents={merged.byLocation.get(id) ?? 0}
                    scale={scale}
                    currency={currency}
                  />
                ))}
            </div>
          )}
          <div className={styles.group}>
            <p className={`${styles.note} ${styles.groupHead}`}>
              Money in counts on the day a payment is received. Invoices not yet paid are not here.
            </p>
          </div>
        </section>

        <section className={styles.card} data-testid="accounting-money-out">
          <div className={styles.cardHead}>
            <h2 className={styles.cardTitle}>Money out</h2>
            <span className={styles.spacer} />
            <b className={styles.num}>{money(moneyOut, currency)}</b>
          </div>

          <div className={styles.outTabs} role="tablist" aria-label="Money out, by kind">
            {(
              [
                ["salaries", "Salaries", merged.salaries],
                ["materials", "Materials", merged.materials],
                ["expenses", "Expenses", merged.expenses],
              ] as const
            )
              .filter(([key]) => key !== "materials" || hasSupply)
              .map(([key, label, cents]) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={outTab === key}
                  className={outTab === key ? `${styles.outTab} ${styles.outTabOn}` : styles.outTab}
                  onClick={() => setOutTab(key)}
                  data-testid={`accounting-out-${key}`}
                >
                  {label} <span className={styles.outTabSum}>{money(cents, currency)}</span>
                </button>
              ))}
          </div>

          {outTab === "salaries" && (
            <div className={styles.group}>
              <GroupHead
                label="Salaries"
                note={partial ? "this selection's share, by rota hours" : undefined}
                cents={merged.salaries}
                currency={currency}
              />
              {/* The longest list here, so it pages to the room (fit every screen size). */}
              <PagedRows rows={people} noun="people">
                {(page) =>
                  page.map(([userId, line]) => (
                    <Line
                      key={userId}
                      label={line.name}
                      sub={personNote(userId, line.job)}
                      cents={line.cents}
                      scale={scale}
                      currency={currency}
                      out
                    />
                  ))
                }
              </PagedRows>
              {!seesPay ? (
                <p className={styles.blank} data-testid="accounting-pay-hidden">
                  Each person's share is shown to someone who may see everyone's salary.
                </p>
              ) : (
                people.length === 0 && <p className={styles.blank}>None</p>
              )}
            </div>
          )}

          {outTab === "materials" && hasSupply && (
            <div className={styles.group}>
              <GroupHead
                label="Materials"
                note="supplier orders received"
                cents={merged.materials}
                currency={currency}
              />
              {suppliers.map(([supplierId, line]) => (
                <Line
                  key={supplierId}
                  label={line.name}
                  cents={line.cents}
                  scale={scale}
                  currency={currency}
                  out
                />
              ))}
              {suppliers.length === 0 && <p className={styles.blank}>None</p>}
            </div>
          )}

          {outTab === "expenses" && (
            <div className={styles.group}>
              <GroupHead label="Expenses" cents={merged.expenses} currency={currency} />
              {spent.map(([categoryId, cents]) => {
                const isOpen = open.has(categoryId);
                const entries = (rows ?? [])
                  .filter((row) => row.categoryId === categoryId && row.countedCents !== 0)
                  .sort((a, b) => a.paidOn.localeCompare(b.paidOn));
                return (
                  <div key={categoryId}>
                    <Line
                      label={categoryName(categoryId)}
                      cents={cents}
                      scale={scale}
                      currency={currency}
                      out
                      open={isOpen}
                      onToggle={() => toggle(categoryId)}
                      testId={`accounting-category-${categoryId}`}
                    />
                    {isOpen && (
                      <div className={styles.entries}>
                        {rows === null ? (
                          <p className={styles.note}>Loading…</p>
                        ) : (
                          entries.map((row) => (
                            <div key={row.id} className={styles.entry}>
                              <span>{niceDayShort(row.paidOn)}</span>
                              <span>
                                {[
                                  row.note ?? "",
                                  row.branchId === null ? "Shared" : locationName(row.branchId),
                                  row.countedCents !== row.amountCents
                                    ? `share of ${money(row.amountCents, currency)}`
                                    : "",
                                ]
                                  .filter((part) => part !== "")
                                  .join(" · ")}
                              </span>
                              <span>{money(row.countedCents, currency)}</span>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {!seesSpending ? (
                <p className={styles.blank} data-testid="accounting-spending-hidden">
                  The expenses behind this total are shown to someone who may see expenses.
                </p>
              ) : (
                spent.length === 0 && <p className={styles.blank}>None</p>
              )}
            </div>
          )}
        </section>
      </div>

      <div className={styles.card}>
        <div className={styles.sumLine} data-testid="accounting-sum-line">
          <span>Money in</span>
          <b>{money(merged.moneyIn, currency)}</b>
          <span className={styles.operator}>−</span>
          <span>Money out</span>
          <b>{money(moneyOut, currency)}</b>
          <span className={styles.operator}>=</span>
          <span>Profit</span>
          <span className={`${styles.big} ${merged.profit < 0 ? styles.loss : styles.profit}`}>
            {money(merged.profit, currency)}
          </span>
        </div>
      </div>
    </>
  );
}

/** "October 2026 so far", "2026", … */
function periodLabel(year: number, month: number, data: AccountingYear): string {
  const current =
    month === 0
      ? data.today.startsWith(`${String(year)}-`)
      : data.months[month - 1]?.current === true;
  const name = month === 0 ? String(year) : `${monthName(month)} ${String(year)}`;
  return `${name}${current ? " so far" : ""}`;
}

function GroupHead({
  label,
  note,
  cents,
  currency,
}: {
  label: string;
  note?: string;
  cents: number;
  currency: string;
}): React.JSX.Element {
  return (
    <div className={styles.groupHead}>
      <span>
        <b className={styles.strong}>{label}</b>
        {note !== undefined && <span className={styles.note}> {note}</span>}
      </span>
      <b className={`${styles.strong} ${styles.num}`}>{money(cents, currency)}</b>
    </div>
  );
}

/**
 * One line of a group, with a bar for its share. A line that opens — an
 * expense category — is a button; the rest are plain rows.
 */
function Line({
  label,
  sub,
  cents,
  scale,
  currency,
  out = false,
  open,
  onToggle,
  testId,
}: {
  label: string;
  sub?: string;
  cents: number;
  scale: number;
  currency: string;
  out?: boolean;
  open?: boolean;
  onToggle?: () => void;
  testId?: string;
}): React.JSX.Element {
  // Never wider than the track, and never invisible for a small amount.
  const width = `${String(Math.min(100, Math.max(0.5, (Math.abs(cents) / scale) * 100)))}%`;
  const content = (
    <>
      <span className={styles.lineLabel}>
        {onToggle !== undefined && (
          <span className={styles.chevron} aria-hidden="true">
            {open === true ? "▾" : "▸"}
          </span>
        )}
        {label}
        {sub !== undefined && <span className={styles.lineSub}>{sub}</span>}
      </span>
      <span className={styles.track} aria-hidden="true">
        <i className={out ? styles.fillOut : styles.fill} style={{ width }} />
      </span>
      <span className={styles.num}>{money(cents, currency)}</span>
    </>
  );

  // `data-row`: what a fitted PagedRows measures a row by.
  if (onToggle === undefined)
    return (
      <div className={styles.line} data-row="">
        {content}
      </div>
    );
  return (
    <button
      type="button"
      className={styles.line}
      aria-expanded={open === true}
      onClick={onToggle}
      data-testid={testId}
    >
      {content}
    </button>
  );
}
